import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

const LOGO_LIGHT = 'https://downsconstructionllc.com/assets/images/logo-light.png';
const LOGO_DARK = 'https://downsconstructionllc.com/assets/images/logo-blue.png';

const serviceLinks = [
  { label: 'Interior & Exterior Renovations', href: '/services/renovations' },
  { label: 'Pole Barns', href: '/services/pole-barns' },
  { label: 'Home Additions', href: '/services/additions' },
  { label: 'General Contracting', href: '/services/general-contracting' },
];

interface NavbarProps {
  onEstimateClick: () => void;
}

export default function Navbar({ onEstimateClick }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSvcOpen, setMobileSvcOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setScrolled(window.scrollY > 60 || !isHome);
  }, [isHome]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const forceScrolled = !isHome || scrolled;
  const linkBase = 'text-sm font-semibold uppercase tracking-wider transition-all duration-200 cursor-pointer whitespace-nowrap relative link-underline';
  const linkColor = forceScrolled ? 'text-gray-800 hover:text-primary' : 'text-white hover:text-primary-light link-underline-light';

  const handleHomeClick = () => {
    if (isHome) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${forceScrolled ? 'bg-white border-b border-gray-100 shadow-sm' : 'bg-transparent'}`}>
      <div className="w-full px-6 sm:px-8 lg:px-16 flex items-center justify-between h-20 lg:h-24">
        {/* Logo — larger on desktop */}
        <button onClick={handleHomeClick} aria-label="Go to home page" className="cursor-pointer flex-shrink-0 btn-press">
          <img
            src={forceScrolled ? LOGO_DARK : LOGO_LIGHT}
            alt="Downs Construction LLC"
            className="h-12 sm:h-14 lg:h-18 w-auto transition-all duration-300"
            style={{ height: 'clamp(3rem, 5vw, 4.5rem)' }}
          />
        </button>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-8 xl:gap-10">
          <button onClick={handleHomeClick} className={`${linkBase} ${linkColor}`}>Home</button>

          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className={`${linkBase} ${linkColor} flex items-center gap-1`}
            >
              Services
              <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {dropdownOpen && (
              <div className="absolute top-full left-0 mt-3 w-72 bg-white border border-gray-100 rounded-lg overflow-hidden shadow-lg animate-scaleIn">
                {serviceLinks.map((s) => (
                  <Link
                    key={s.label}
                    to={s.href}
                    onClick={() => setDropdownOpen(false)}
                    className="flex items-center gap-2 px-5 py-3.5 text-sm text-gray-700 hover:bg-primary-light hover:text-primary transition-colors duration-200 cursor-pointer border-b border-gray-50 last:border-0"
                  >
                    <i className="ri-arrow-right-s-line text-primary text-base flex-shrink-0"></i>
                    {s.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link to="/about" className={`${linkBase} ${linkColor}`}>About Us</Link>
          <Link to="/contact" className={`${linkBase} ${linkColor}`}>Contact</Link>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={onEstimateClick}
            className="hidden lg:block bg-primary hover:bg-primary-dark text-white text-xs font-bold uppercase tracking-widest px-5 py-3 rounded-md transition-all duration-200 cursor-pointer whitespace-nowrap btn-press animate-pulse-glow"
          >
            Free Estimate
          </button>
          <button
            className={`lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer rounded-md transition-all duration-200 ${forceScrolled ? 'text-gray-800 hover:bg-primary-light' : 'text-white hover:bg-white/10'}`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle mobile menu"
          >
            <i className={`text-2xl transition-all duration-200 ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`lg:hidden bg-white border-t border-gray-100 overflow-hidden transition-all duration-300 ${mobileOpen ? 'max-h-[520px] shadow-lg' : 'max-h-0'}`}>
        <div className="px-6 py-5 flex flex-col gap-4">
          <button onClick={() => { setMobileOpen(false); handleHomeClick(); }} className="text-sm font-semibold uppercase tracking-wider text-gray-800 hover:text-primary cursor-pointer text-left transition-colors duration-200">Home</button>
          <div>
            <button
              onClick={() => setMobileSvcOpen(!mobileSvcOpen)}
              className="text-sm font-semibold uppercase tracking-wider text-gray-800 hover:text-primary flex items-center gap-1 cursor-pointer whitespace-nowrap w-full text-left transition-colors duration-200"
            >
              Services
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileSvcOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {mobileSvcOpen && (
              <div className="mt-2 pl-4 flex flex-col gap-2 border-l-2 border-primary-light">
                {serviceLinks.map((s) => (
                  <Link key={s.label} to={s.href} className="text-sm text-gray-600 hover:text-primary cursor-pointer py-1 transition-colors duration-200" onClick={() => { setMobileOpen(false); setMobileSvcOpen(false); }}>
                    {s.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
          <Link to="/about" className="text-sm font-semibold uppercase tracking-wider text-gray-800 hover:text-primary cursor-pointer transition-colors duration-200" onClick={() => setMobileOpen(false)}>About Us</Link>
          <Link to="/contact" className="text-sm font-semibold uppercase tracking-wider text-gray-800 hover:text-primary cursor-pointer transition-colors duration-200" onClick={() => setMobileOpen(false)}>Contact</Link>
          <button
            onClick={() => { setMobileOpen(false); onEstimateClick(); }}
            className="bg-primary hover:bg-primary-dark text-white text-xs font-bold uppercase tracking-widest px-5 py-3.5 rounded-md cursor-pointer whitespace-nowrap w-full transition-colors duration-200 btn-press"
          >
            Free Estimate
          </button>
        </div>
      </div>
    </nav>
  );
}
